/**
 * Is the news feed actually working?
 *
 * The question this answers is not "did anything publish" — a quiet market and
 * a dead feed both look quiet. `docs/05`: *"a feed that goes quiet must look
 * different from a market that goes quiet."* That distinction is the screen.
 *
 * The two things to read first, in order:
 *
 * 1. **A source at `stale`.** It has not parsed a usable payload in several of
 *    its own patrol intervals. An exhausted vendor quota and a slow news week
 *    both answer HTTP 200, so status is the only field that tells them apart.
 * 2. **The quota row.** A credential at its cap has stopped polling for the
 *    rest of the UTC day. Since Phase 54a that count survives restarts, so a
 *    low number after a deploy is the truth rather than amnesia.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api, ApiError, fmtSince, type NewsStatusResponse } from '../../lib/api.js';
import type { ResolvedRange, SnapshotDay } from '@aegis/api-client';
import { RangePicker } from '../../components/RangePicker.js';

const TONE: Record<string, string> = {
  ok: 'chip-ok',
  stale: 'chip-caution',
  error: 'chip-breached',
  disabled: '',
  pending: '',
};

export function NewsStatusScreen(): JSX.Element {
  const [data, setData] = useState<NewsStatusResponse | null>(null);
  const [history, setHistory] = useState<readonly SnapshotDay<NewsStatusResponse>[]>([]);
  const [window, setWindow] = useState<ResolvedRange | null>(null);
  const [range, setRange] = useState('7d');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (r: string) => {
    try {
      const res = await api.newsStatus(r);
      setData(res);
      setHistory(res.history as readonly SnapshotDay<NewsStatusResponse>[]);
      setWindow(res.range);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404
        ? 'This is not available on your account.'
        : 'Could not load news status.');
    }
  }, []);
  useEffect(() => { void load(range); }, [load, range]);

  if (!data) {
    return (
      <div className="scroll-area">
        <h2>News status</h2>
        <p className="dim">{error ?? 'Loading…'}</p>
      </div>
    );
  }

  const unwell = data.sources.filter((s) => s.status === 'stale' || s.status === 'error');
  // A disabled source is not a fault — it has no key — but it is also not
  // "ok", and a summary that called thirteen sources fine while five of them
  // were switched off would be the one line on this screen nobody could trust.
  const off = data.sources.filter((s) => s.status === 'disabled');
  const summary = unwell.length > 0
    ? { tone: 'chip-caution', text: `${unwell.length} needing attention` }
    : off.length > 0
      ? { tone: '', text: `${data.sources.length - off.length} of ${data.sources.length} live` }
      : { tone: 'chip-ok', text: 'all sources ok' };

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>News status</h2>
        <span className={`chip ${summary.tone}`}>{summary.text}</span>
      </div>

      <p className="panel-body dim">
        A source reads <strong>stale</strong> when it has not parsed anything in
        several of its own patrol intervals — which is how an exhausted quota is
        told apart from a slow news week, since both answer 200.
        {off.length > 0 ? ` ${off.length} are disabled for want of an API key.` : ''}
      </p>

      {/* Quota first: it is the thing that silently stops a source, and the
          number that used to reset on every deploy. */}
      <section className="admin-block">
        <h3 className="panel-h">VENDOR CALLS TODAY</h3>
        {Object.keys(data.budgetUsed).length === 0 ? (
          <p className="panel-body dim">Nothing charged yet today.</p>
        ) : (
          <dl className="kv">
            {Object.entries(data.budgetUsed).map(([key, used]) => (
              <span key={key} className="kv-pair">
                <dt>{key}</dt>
                <dd className="mono">{used}</dd>
              </span>
            ))}
          </dl>
        )}
      </section>

      <section className="admin-block">
        <h3 className="panel-h">
          SOURCES · {data.sources.length} · feed holds {data.feedSize}
          {data.merges > 0 ? ` · ${data.merges} folded` : ''}
        </h3>
        <ul className="admin-list">
          {data.sources.map((s) => (
            <li key={s.id} className="admin-row">
              <span className={`chip ${TONE[s.status] ?? ''}`}>{s.status}</span>
              <span className="admin-name">{s.id}</span>
              <span className="admin-meta">
                {s.scheduling}
                {s.armed ? ' · armed' : ''}
                {s.polls > 0 ? ` · ${s.polls} polls` : ''}
                {s.consecutiveFailures > 0 ? ` · ${s.consecutiveFailures} failing` : ''}
                {/* A generated calendar that has run out is not an error yet
                    and is not fine either: the watcher falls back to a patrol
                    on the next release, and the fix is `pnpm news:calendar`. */}
                {s.calendarExhausted ? ' · calendar exhausted' : ''}
              </span>
              <span className="admin-when">
                {s.lastParsedAt ? fmtSince(s.lastParsedAt) : 'never parsed'}
              </span>
              {s.lastError ? <p className="admin-error">{s.lastError}</p> : null}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <div className="section-head"><h3>Over time</h3></div>
        <RangePicker
          value={range}
          onChange={setRange}
          resolved={window}
          note="Recorded once a day. History begins when snapshots were switched on."
        />
        {history.length === 0 ? (
          <p className="panel-body dim">
            Nothing recorded in this window. Snapshots start the day they were
            deployed — earlier dates have no rows rather than reconstructed ones.
          </p>
        ) : (
          <div className="table-scroll" tabIndex={0}>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th className="num">Sources</th>
                  <th className="num">Failing</th>
                  <th className="num">Feed</th>
                </tr>
              </thead>
              <tbody>
                {history.map((d) => {
                  const sources = d.payload.sources ?? [];
                  // A source that has been failing repeatedly is the number
                  // worth a column: one bad poll is weather, five is a broken
                  // key or a changed feed.
                  const failing = sources.filter((x) => x.consecutiveFailures > 0).length;
                  return (
                    <tr key={d.day}>
                      <td className="mono">{d.day}</td>
                      <td className="num mono">{sources.length}</td>
                      <td className="num mono">{failing || '—'}</td>
                      <td className="num mono">{d.payload.feedSize ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
